import type { ProductSelectOption, ProductSelectStatus } from '@/components/products/ProductSelect';

function parseProductStatus(value: unknown): ProductSelectStatus | undefined {
  if (value === 'ACTIVE' || value === 'DORMANT' || value === 'DELISTED' || value === 'UNMANAGED') {
    return value;
  }
  return undefined;
}

function optionalString(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value : null;
}

export function toProductSelectOption(raw: unknown): ProductSelectOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const row = raw as Record<string, unknown>;
  if (typeof row.id !== 'string' || typeof row.sku !== 'string' || typeof row.name !== 'string') {
    return null;
  }

  return {
    id: row.id,
    sku: row.sku,
    name: row.name,
    barcode: optionalString(row.barcode),
    unitBarcode: optionalString(row.unitBarcode),
    caseBarcode: optionalString(row.caseBarcode),
    unitsPerCase: typeof row.unitsPerCase === 'number' ? row.unitsPerCase : null,
    thumbnailUrl: optionalString(row.thumbnailUrl),
    imageUrl: optionalString(row.imageUrl),
    media: Array.isArray(row.media)
      ? (row.media as ProductSelectOption['media'])
      : null,
    status: parseProductStatus(row.status),
  };
}

export type CatalogProductAvailability = {
  isActive?: boolean | null;
  status?: string | null;
  deletedAt?: string | Date | null;
};

/** True when a slot product can be restocked from the catalog. */
export function isCatalogProductActive(
  product: CatalogProductAvailability | null | undefined
): boolean {
  if (!product) return false;
  if (product.deletedAt) return false;
  if (product.isActive === false) return false;
  if (product.status === 'DORMANT' || product.status === 'DELISTED' || product.status === 'UNMANAGED') {
    return false;
  }
  return true;
}

export function parseProductSelectOptions(data: unknown): ProductSelectOption[] {
  const rows = Array.isArray(data)
    ? data
    : data && typeof data === 'object' && 'items' in data && Array.isArray((data as { items: unknown }).items)
      ? (data as { items: unknown[] }).items
      : [];

  return rows
    .map((row) => toProductSelectOption(row))
    .filter((row): row is ProductSelectOption => row !== null);
}
